/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { type Registry as Services, service } from '@ember/service';
import type { IReactElement } from 'uplisting-frontend/services/react-remote';

interface IArgs {
  element: string;
  props?: Record<string, unknown>;
}

interface ReactWidgetSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class ReactWidgetComponent extends Component<ReactWidgetSignature> {
  @service reactRemote!: Services['react-remote'];
  @service sentry!: Services['sentry'];

  @tracked unavailable = false;

  #element?: IReactElement;
  #props: Record<string, unknown> = {};

  // Creating the element imperatively rather than writing the tag in the
  // template: the tag name is an argument, and Glimmer has no dynamic element
  // syntax. It also keeps the "remote is defined before the tag is upgraded"
  // ordering in one place.
  mount = modifier((container: HTMLDivElement) => {
    let cancelled = false;

    void this.attach(container, () => cancelled);

    return () => {
      cancelled = true;

      this.#element?.remove();
      this.#element = undefined;
    };
  });

  // A separate modifier so a props change re-runs only this one. Consuming
  // `@props` inside `mount` would tear the element down and remount React on
  // every update.
  syncProps = modifier(
    (_element: HTMLDivElement, [props]: [IArgs['props']]) => {
      this.#props = props ?? {};

      if (this.#element) {
        this.#element.props = this.#props;
      }
    },
  );

  // Re-rendering the branch remounts the element, and the service drops its
  // cached load on failure, so clearing the flag is a real retry.
  @action
  retry(): void {
    this.unavailable = false;
  }

  private async attach(
    container: HTMLDivElement,
    cancelled: () => boolean,
  ): Promise<void> {
    try {
      await this.reactRemote.defineElements();

      if (cancelled()) {
        return;
      }

      const element = window.document.createElement(
        this.args.element,
      ) as IReactElement;

      element.hostConfig = this.reactRemote.hostConfig;
      element.props = this.#props;

      this.#element = element;
      container.append(element);
    } catch (error) {
      this.sentry.captureException(error);

      this.unavailable = true;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    ReactWidget: typeof ReactWidgetComponent;
  }
}
